import { get } from 'lodash';
import { canViewCommissionPayoutReport } from '../common/helpers/reports';
import { shouldOpenPaymentProcessingDashboard } from '../main/settings/selectors';
import {
  activeShop,
  brandRewardsEnabled,
  getEngagementOnboarded,
  getIsRentalBarber,
  isBarberUser,
  isUserOfRole,
  isBrandViewActive,
  canActiveBrandUseMemberships,
  canActiveShopUseMemberships,
  canSellGiftCards,
} from './selectors';
import { setFrontendFlags } from './actions';
import { USER_TYPES } from './constants';
import * as checker from '../common/helpers/users';
import { setDate } from '../main/appointments/actions';
import {
  canUseEcommerce,
  canUseMailchimp,
  isBrandOrSingleShopBrand,
  canUserViewDashboard,
  canUserViewPromos,
  canUserViewReports,
  canUserViewStaffManagement,
  canUserViewBarbers,
  canUseEngage,
} from '../common/helpers/users';

import { CONNECT, INTEGRATIONS_PATH, MAILCHIMP_PATH } from '../main/settings/integrations/constants';
import { handoffResetKioskCart } from '../handoff/actions';
import { shouldNotifyKioskDevice } from '../kioskCartSync/selectors';

import {
  ADMIN_PATH,
  APPOINTMENTS_PATH,
  BARBER_PATH,
  BRAND_PATH,
  CHANGE_PASSWORD_BRAND_PATH,
  DASHBOARD_RELATIVE_PATH,
  FBE_LOGIN_PATH,
  LOGIN_PATH,
  LOYALTY_BRAND_PATH,
  PROMOS_BRAND_PATH,
  ROOT_PATH,
  SALES_RELATIVE_PATH,
} from '../../paths';
import { FullstoryService } from '../../modules/infra/observability/fullstory';
import { waitForFeatureFlags } from '../../modules/infra/feature-flags';

export const requireAuth = (store) => (nextState, replace, callback) => {
  if (!store.getState().auth.token) {
    replace({
      pathname: LOGIN_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  callback();
};

const shouldReturnToInitialPath = (nextLocation) => {
  if (!nextLocation.query || !nextLocation.state) {
    return false;
  }

  const shouldPreserveLocation = nextLocation.query.preserveLocation === 'true';
  const isIntegrationsPath = nextLocation.state.nextPathname.includes('settings/integrations');

  if (!(shouldPreserveLocation || isIntegrationsPath)) {
    return false;
  }

  return !!nextLocation.state.nextPathname;
};

const prepareRedirectQuery = (nextLocation) => {
  if (!shouldReturnToInitialPath(nextLocation)) {
    return nextLocation.query;
  }

  const initialQuery = { ...nextLocation.query };
  const { preserveLocation, ...restQuery } = initialQuery;

  return {
    ...restQuery,
    redirectTo: nextLocation.state.nextPathname,
  };
};

export const requireAuthAndRedirect = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!state.auth.token) {
    replace({
      pathname: LOGIN_PATH,
      state: { nextPathname: nextState.location.pathname },
      query: prepareRedirectQuery({
        ...nextState.location,
        state: {
          nextPathname: nextState.location.pathname,
          ...(nextState.location.state || {}),
        },
      }),
    });
  } else {
    switch (get(state.auth.user, 'kind')) {
      case USER_TYPES.shop: {
        if (state.auth.activeType === 'brand') {
          const shouldPreserveIntegrations = nextState.location.pathname.startsWith('/settings/integrations');

          replace({
            pathname: shouldPreserveIntegrations ? `${BRAND_PATH}${nextState.location.pathname}` : BRAND_PATH,
            state: { nextPathname: nextState.location.pathname },
            query: nextState.location.query,
          });
        }
        break;
      }
      case USER_TYPES.admin:
      case USER_TYPES.regularAdmin:
        replace({
          pathname: ADMIN_PATH,
          state: { nextPathname: nextState.location.pathname },
        });
        break;
      case USER_TYPES.barber:
        replace({
          pathname: BARBER_PATH,
          state: { nextPathname: nextState.location.pathname },
          query: nextState.location.query,
        });
        break;
      default:
    }
  }
  callback();
};

export const redirectAuth = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  switch (get(state.auth.user, 'kind')) {
    case USER_TYPES.admin:
    case USER_TYPES.regularAdmin:
    case USER_TYPES.agent:
      replace({
        pathname: ADMIN_PATH,
        state: { nextPathname: nextState.location.pathname },
      });
      break;
    case USER_TYPES.shop: {
      if (state.auth.activeType === 'brand') {
        replace({
          pathname: BRAND_PATH,
          state: { nextPathname: nextState.location.pathname },
          query: nextState.location.query,
        });
      } else {
        replace({
          pathname: APPOINTMENTS_PATH,
          state: { nextPathname: nextState.location.pathname },
          query: nextState.location.query,
        });
      }

      break;
    }
    case USER_TYPES.barber:
      replace({
        pathname: BARBER_PATH,
        state: { nextPathname: nextState.location.pathname },
        query: nextState.location.query,
      });
      break;
    default:
  }
  callback();
};

const requireUserType = (userType) => (store) => (nextState, replace, callback) => {
  const state = store.getState();

  if (!isUserOfRole(state, userType)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
      query: nextState.location.query,
    });
  }
  callback();
};

export const requireAdmin = requireUserType([USER_TYPES.admin, USER_TYPES.regularAdmin, USER_TYPES.agent]);

export const requireShop = requireUserType(USER_TYPES.shop);

export const requireBarber = requireUserType(USER_TYPES.barber);

export const requireGiftCardSellerConditions = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!canSellGiftCards(state)) {
    replace({
      pathname: '/',
      state: { nextPathname: nextState.location.pathname },
    });
  }

  callback();
};

export const requireBrand = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  const doesUserHaveBrand = !!get(state.auth.user, 'brand');

  if (!doesUserHaveBrand || !isBrandViewActive(state)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  callback();
};

export const requireServiceViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!checker.canUserViewServices(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  callback();
};

export const requireClientsViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!checker.canUserViewCustomers(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  callback();
};

export const requireReportsViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!checker.canUserViewReports(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  callback();
};

export const requireTimeReportsViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!checker.canUserViewTimeReport(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  callback();
};

export const requireAnalyticsViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();

  if (!checker.canUserViewAnalytics(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  callback();
};

export const requireCommissionSplitReportAbility = (store) => (nextState, replace, callback) => {
  const state = store.getState();

  if (!canViewCommissionPayoutReport(state)) {
    replace({
      pathname: '/',
      state: { nextPathname: nextState.location.pathname },
    });
  }

  callback();
};

export const requireServiceModifyPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!checker.canUserModifyServices(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  callback();
};

export const requireServiceCategoriesPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  const canModifyOrAddServices =
    checker.canUserModifyServices(state.auth.permissions) || checker.canUserAddServices(state.auth.permissions);
  const hasEnoughPermissions = checker.canUserViewServices(state.auth.permissions) && canModifyOrAddServices;
  // routes are accessible for all rental barbers because we don't have property specifying indy
  const isBarberButNotRental = isBarberUser(state) && !getIsRentalBarber(state);

  if (!hasEnoughPermissions || isBarberButNotRental) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  callback();
};

export const requireServiceAddPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!checker.canUserAddServices(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  return callback();
};

export const checkSettingsPermissions = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  const canUserViewBankAccounts =
    nextState.params.page === 'bank-account' && !checker.canUserViewBankAccounts(state.auth.permissions);
  const canUserViewBarberSettings =
    nextState.params.page === 'barber' && !checker.canUserViewBarberSettings(state.auth.permissions);
  if (canUserViewBankAccounts || canUserViewBarberSettings) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  return callback();
};

export const requireBankAccountModifyPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!checker.canUserModifyBankAccounts(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  return callback();
};

export const requireBarberViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!checker.canUserViewBarbers(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  return callback();
};

export const requireBarberModifyPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!checker.canUserModifyBarbers(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  return callback();
};

export const requireDashboardViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();

  if (!checker.canUserViewDashboard(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const requireProductsViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();

  if (!checker.canUserViewProducts(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const requireProductsAddPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  const canModifyProducts = checker.canUserModifyProducts(state.auth.permissions);
  const isCommissionBarber = isBarberUser(state) && !getIsRentalBarber(state);

  if (!canModifyProducts || isCommissionBarber) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const requireNotBarberAndProductsPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  const canModifyProducts = checker.notBarberAndCanModifyProducts(state);

  if (!canModifyProducts) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const requireProductsModifyPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();

  if (!checker.canUserModifyProducts(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const resetAppointmentsDate = (store) => () => {
  store.dispatch(setDate(''));
};

export const requireRegisterViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();

  if (!checker.canUserViewRegister(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const requireCashRegisterViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();

  if (!checker.canUserViewCashRegister(state.auth.permissions, getIsRentalBarber(state))) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const requireDailySummaryViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  const { user, permissions, activeShop: shop } = state.auth;

  if (!checker.canUserViewDailySummary(user, shop, permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const requireWaitingListViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();

  if (!checker.canUserViewWaitingList(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const requireCustomChargeViewPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!checker.canUserViewCustomCharge(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  return callback();
};

export const setEngagementOnboarded = (store) => {
  if (!getEngagementOnboarded(store.getState())) {
    store.dispatch(setFrontendFlags({ engagementOnboarded: true }));
  }
};

export const requireEngagePermission = (store) => async (nextState, replace, callback) => {
  const state = store.getState();
  const hasEngagePermission = checker.canUserViewEngage(state.auth.permissions) && canUseEngage(state);

  const isBarber = isBarberUser(state);
  const { engageForBarbers, engageForChairRenters } = await waitForFeatureFlags();
  const isEngageFeatureFlagEnabled = !isBarber || engageForBarbers || engageForChairRenters;

  if (!hasEngagePermission || !isEngageFeatureFlagEnabled) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  } else {
    setEngagementOnboarded(store);
  }

  return callback();
};

export const redirectNewCampaignsToEngageBuilderV2 = () => async (nextState, replace, callback) => {
  const newCampaignPath = nextState.location.pathname.replace('/engagement/new', '/engagement-v2/new/type');
  replace({ pathname: newCampaignPath });

  return callback();
};

export const requireEnabledBoothRental = (store) => async (nextState, replace, callback) => {
  const { hybridExposeRentCollect } = await waitForFeatureFlags();

  if (!checker.canUseChairRental(activeShop(store.getState()), hybridExposeRentCollect)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const requireEditableCustomReports = (store) => (nextState, replace, callback) => {
  const state = store.getState();

  if (!checker.canUserEditCustomReports(state)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const requireViewableCustomReports = (store) => (nextState, replace, callback) => {
  const state = store.getState();

  if (!checker.canUserViewCustomReports(state)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  return callback();
};

export const requireBrandAccessToMemberships = (store) => (nextState, replace, callback) => {
  if (!canActiveBrandUseMemberships(store.getState())) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  return callback();
};

export const requireShopAccessToMemberships = (store) => (nextState, replace, callback) => {
  if (!canActiveShopUseMemberships(store.getState())) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  return callback();
};

export const paymentProcessingGuard =
  (store, actualPaymentProcessingRoute, onEnterHook) => (nextState, replace, callback) => {
    const state = store.getState();

    const isDashboardEnabled = shouldOpenPaymentProcessingDashboard(state);
    const isBankAccountPage = nextState.location.pathname.includes('bank-account');
    const shouldOpenNewPaymentProcessing = actualPaymentProcessingRoute.includes('payment-processing');
    const isPaymentProcessingPage = nextState.location.pathname.includes('payment-processing');

    const shouldRedirect = isDashboardEnabled
      ? shouldOpenNewPaymentProcessing && isBankAccountPage
      : isPaymentProcessingPage;

    if (shouldRedirect) {
      replace({
        pathname: actualPaymentProcessingRoute,
        state: { nextPathname: nextState.location.pathname },
        query: nextState.location.query,
      });
    }

    if (onEnterHook) {
      return onEnterHook(store)(nextState, replace, callback);
    }

    return callback();
  };

export const redirectByPermissionCheck = (store, permissionCheck, successUrl, failedUrl) =>
  function onEnter(nextState, replace, callback) {
    const baseUrl = nextState.location.pathname;

    if (baseUrl.endsWith(this.path)) {
      const state = store.getState();
      const redirectTo = permissionCheck(state.auth.permissions) ? successUrl : failedUrl;

      replace({ pathname: `${baseUrl}/${redirectTo}` });
    }

    return callback();
  };

export const brandPromosRedirectByPermissionCheck = (store) =>
  function onEnter(nextState, replace, callback) {
    const baseUrl = nextState.location.pathname;
    const state = store.getState();
    const hasAccess = canUserViewPromos(state.auth.permissions);
    const loyaltyEnabled = brandRewardsEnabled(state);
    const isOnLoyaltyBasePath = baseUrl.includes(LOYALTY_BRAND_PATH);
    const isOnPromoBasePath = baseUrl.includes(PROMOS_BRAND_PATH);
    const needRedirectFromLoyalty = isOnLoyaltyBasePath && !loyaltyEnabled;
    const needToRedirect = !isOnPromoBasePath && (!isOnLoyaltyBasePath || needRedirectFromLoyalty);

    if (!hasAccess) {
      replace({ pathname: CHANGE_PASSWORD_BRAND_PATH });

      return callback();
    }

    if (needToRedirect) {
      replace({ pathname: PROMOS_BRAND_PATH });
    }

    return callback();
  };

export const brandReportsRedirectByPermissionCheck = (store) =>
  function onEnter(nextState, replace, callback) {
    const baseUrl = nextState.location.pathname;
    const state = store.getState();
    const hasAccessToReports = canUserViewReports(state.auth.permissions);
    const hasAccessToDashboard = canUserViewDashboard(state.auth.permissions);
    const defaultPath = hasAccessToDashboard ? DASHBOARD_RELATIVE_PATH : SALES_RELATIVE_PATH;

    if (!hasAccessToReports) {
      replace({ pathname: CHANGE_PASSWORD_BRAND_PATH });

      return callback();
    }

    if (baseUrl.endsWith(this.path)) {
      const redirectTo = `${baseUrl}/${defaultPath}`;

      replace({ pathname: redirectTo });
    }

    return callback();
  };

export const requireProductsEcommercePermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  const canUserViewProducts = checker.canUserViewProducts(state.auth.permissions);
  const isEcommerceEnabled = canUseEcommerce(state);

  if (!canUserViewProducts || !isEcommerceEnabled) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const requireIntegrationPermission = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  const canUseEcommerceOrMailchimp = canUseEcommerce(state) || canUseMailchimp(state);
  const hasAccessToIntegrations = canUseEcommerceOrMailchimp && isBrandOrSingleShopBrand(state);

  if (!hasAccessToIntegrations) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }
  return callback();
};

export const requireAuthAndMailchimpPermissions = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  const hasAccessToMailchimp = canUseMailchimp(state) && isBrandOrSingleShopBrand(state);

  if (!hasAccessToMailchimp) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  const baseUrl = isBrandViewActive(state) ? '/brand' : '';

  replace({
    pathname: `${baseUrl}${INTEGRATIONS_PATH}${MAILCHIMP_PATH}/${CONNECT}`,
    state: { nextPathname: nextState.location.pathname },
    query: nextState.location.query,
  });

  return callback();
};

export const handleRegisterOnLeave = (store) => () => {
  if (shouldNotifyKioskDevice(store.getState())) {
    store.dispatch(handoffResetKioskCart());
  }
};

export const requireStaffManagementAvailability = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!canUserViewStaffManagement(state)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const requireDeletedStaffListAvailability = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  if (!canUserViewStaffManagement(state) && !canUserViewBarbers(state.auth.permissions)) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};

export const initFullstoryFeature = (featureName) => () => {
  FullstoryService.initForFeature(featureName);
};

export const requireAuthForFbeLogin = (store) => (nextState, replace, callback) => {
  const state = store.getState();
  const { token } = state.auth;
  const { location } = nextState;

  if (!token) {
    replace({
      pathname: FBE_LOGIN_PATH,
      query: {
        ...location.query,
      },
    });
  }

  callback();
};

export const requirePhoneAgentPermission = (store) => async (nextState, replace, callback) => {
  const { aiPhoneAssistant } = await waitForFeatureFlags();
  const state = store.getState();
  const shopEmail = state.auth?.activeShop?.email;

  if (!aiPhoneAssistant || !shopEmail) {
    replace({
      pathname: ROOT_PATH,
      state: { nextPathname: nextState.location.pathname },
    });
  }

  return callback();
};
